/**
 * Post-Completion workflow tab on the Case File.
 *
 * Shows every SDLT / AP1 / MR01 / redemption / leasehold-notice item
 * with status, statutory deadline countdown, submission ref, notes and
 * an event timeline. Solicitors can:
 *   - "Init" to bootstrap the default set of items for this case
 *   - Submit via the (currently mock) provider adapter
 *   - Manually patch status / refs / notes once they have real
 *     confirmation from HMRC / HMLR / Companies House etc.
 *
 * Why is this its own tab? Post-completion has its own SLA clock
 * (SDLT = 14 days, AP1 = ~30 working days, MR01 = 21 days) that's
 * easy to miss when buried inside Tasks. A dedicated surface keeps
 * statutory deadlines unmistakable.
 *
 * This file intentionally stays slim — orchestration only. Per-item
 * concerns live in ./post-completion/*. See ItemCard.jsx for the
 * heavy-lifting card UI.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Stamp, Plus, FileText } from "@phosphor-icons/react";
import { KIND_META } from "./post-completion/constants";
import ItemCard from "./post-completion/ItemCard";
import SummaryStat from "./post-completion/SummaryStat";

export default function PostCompletionTab({ caseId, caseData, isSolicitor }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], summary: {}, completion_date: null });
  const [adding, setAdding] = useState(false);
  const [addKind, setAddKind] = useState("");
  const [generatingStatement, setGeneratingStatement] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/post-completion`);
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load post-completion");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const handleInit = async () => {
    try {
      const { data: resp } = await API.post(`/cases/${caseId}/post-completion/init`);
      setData((d) => ({ ...d, items: resp.items, summary: resp.summary }));
      if (resp.inserted === 0) toast.info("All default workflows are already on this case.");
      else toast.success(`Seeded ${resp.inserted} workflow${resp.inserted === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initialise");
    }
  };

  const handleAdd = async () => {
    if (!addKind) return;
    setAdding(true);
    try {
      const { data: item } = await API.post(`/cases/${caseId}/post-completion/items`, { kind: addKind });
      setData((d) => ({ ...d, items: [...d.items, item] }));
      setAddKind("");
      toast.success("Workflow added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  // Generate (and auto-download) the case completion statement. The
  // server-side endpoint already auto-files a dated copy into the case
  // "Comp Docs" folder — so this is "click to download + archive".
  const handleGenerateCompletionStatement = async () => {
    setGeneratingStatement(true);
    const tid = toast.loading("Generating completion statement…");
    try {
      const ts = Date.now();
      const resp = await API.get(
        `/cases/${caseId}/fees/completion-statement.pdf?t=${ts}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "completion-statement.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Generated — auto-filed under Documents › Comp Docs", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed", { id: tid });
    } finally {
      setGeneratingStatement(false);
    }
  };

  const handleItemChange = (updated) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === updated.id ? updated : i)) }));
  };
  const handleItemDelete = (id) => {
    setData((d) => ({ ...d, items: d.items.filter((i) => i.id !== id) }));
  };

  const sortedItems = useMemo(() => {
    const order = ["sdlt", "ap1", "mr01", "redemption", "leasehold_notice"];
    return [...data.items].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  }, [data.items]);

  const availableToAdd = useMemo(() => {
    const have = new Set(data.items.map((i) => i.kind));
    return Object.keys(KIND_META).filter((k) => !have.has(k));
  }, [data.items]);

  if (loading) {
    return <div className="text-sm text-slate-500 p-6">Loading post-completion workflows…</div>;
  }

  const empty = data.items.length === 0;

  return (
    <div className="space-y-6" data-testid="post-completion-tab">
      {/* Summary header */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Post-Completion
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Track statutory submissions — SDLT, AP1, MR01, redemption, leasehold notices.
              {" "}<span className="text-slate-600">MR01 files directly with Companies House and HMRC SDLT files via the Stamp Taxes gateway (per your Submission Settings).</span>{" "}
              <span className="text-amber-700 font-semibold">HMLR AP1 and the manual notices still run in mock mode</span> until those credentials arrive.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleGenerateCompletionStatement}
              disabled={generatingStatement}
              variant="outline"
              className="border-[#1462a0]/40 text-[#1462a0] hover:bg-[#1462a0]/10"
              data-testid="pc-completion-statement-btn"
            >
              <FileText weight="bold" className="w-4 h-4 mr-1" />
              {generatingStatement ? "Generating…" : "Completion Statement"}
            </Button>
            {isSolicitor && empty && (
              <Button onClick={handleInit} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="pc-init-btn">
                <Plus weight="bold" className="w-4 h-4 mr-1" />
                Seed default workflows
              </Button>
            )}
          </div>
        </div>
        {!empty && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <SummaryStat label="Pending" value={data.summary.pending} tone="slate" testid="pc-summary-pending" />
            <SummaryStat
              label="Overdue"
              value={data.summary.overdue}
              tone={data.summary.overdue > 0 ? "rose" : "slate"}
              testid="pc-summary-overdue"
            />
            <SummaryStat label="Completed" value={data.summary.completed} tone="emerald" testid="pc-summary-completed" />
            <div className="border border-slate-200 rounded-md p-3" data-testid="pc-summary-nearest">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Nearest deadline</div>
              {data.summary.nearest_deadline ? (
                <div className="text-sm text-slate-900 mt-1">
                  <div className="font-semibold">
                    {KIND_META[data.summary.nearest_deadline.kind]?.label || data.summary.nearest_deadline.kind}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(data.summary.nearest_deadline.deadline_date).toLocaleDateString("en-GB")}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic mt-1">None pending</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Workflow cards */}
      {empty ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <Stamp weight="duotone" className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No post-completion workflows yet.</p>
          <p className="text-xs text-slate-500 mt-1">
            They&apos;re created automatically when the Completion stage is ticked, or click &quot;Seed default workflows&quot; above.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedItems.map((item) => (
            <ItemCard
              key={item.id}
              caseId={caseId}
              caseData={caseData}
              item={item}
              onChange={handleItemChange}
              onDelete={handleItemDelete}
            />
          ))}
        </div>
      )}

      {/* Manual add */}
      {isSolicitor && availableToAdd.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1 min-w-[240px]">
            <Label className="text-xs">Add a workflow manually</Label>
            <Select value={addKind} onValueChange={setAddKind}>
              <SelectTrigger className="h-9 text-sm" data-testid="pc-add-kind-select">
                <SelectValue placeholder="Choose a workflow…" />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((k) => (
                  <SelectItem key={k} value={k}>{KIND_META[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAdd} disabled={!addKind || adding} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="pc-add-btn">
            <Plus weight="bold" className="w-4 h-4 mr-1" />
            {adding ? "Adding…" : "Add workflow"}
          </Button>
        </div>
      )}
    </div>
  );
}
